import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { MDXProvider } from '@mdx-js/react'
import { Container, Row, RowColumn, List } from 'chaoskit/src/components'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import BaseLayout from './BaseLayout'

import StaticFooter from '~partials/footer/StaticFooter'
import Hero, {
  HeroContent,
  HeroFormWrapper,
  HeroTitle,
  HeroFormTitle,
} from '~partials/Hero'
import TeamIntro from '~partials/home/TeamIntro'
import Data from '~components/Data'
import Image from '~components/Image'
import FooterCTA from '~partials/footer/FooterCTA'
import ProductOverview from '~partials/product/ProductOverview'
import HelmetSEO from '~partials/global/HelmetSEO'
import Quote from '~components/Quote'
import { listBullet } from '~styles/utility'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import track from '~helpers/track'
import MarketoForm from '~components/MarketoForm'
import { ReactComponent as DownloadSVG } from '~media/chaos-engineering/download-book.svg'
import BackgroundSVG from '~media/chaos-engineering/book-background.svg'
import BackgroundMobileSVG from '~media/chaos-engineering/book-background-mobile.svg'
import CaretDoubleSVG from '~icons/caret-double.svg'

const BookLandingPageLayout = ({
  article: {
    frontmatter: {
      seoTitle,
      description,
      socialMediaImage,
      hero,
      leftColumnContent,
      rightColumnContent,
      showProductOverview,
      showFooterCTA,
      hideTeamIntro,
      bookImage,
    },
  },
  nodeType,
}) => {
  const theme = useTheme()

  return (
    <BaseLayout
      noFooter
      headerProps={{
        contrast: false,
        showPrimaryLogo: true,
        sticky: false,
        showNav: false,
      }}
    >
      <HelmetSEO
        title={seoTitle}
        description={description}
        image={socialMediaImage}
        noIndex
      />
      <NodeTypeTracker nodeType={nodeType} />
      <Hero contrast={false}>
        <HeroContent>
          <Container>
            <RowColumn size={{ medium: 6 }}>
              <HeroTitle
                as="h3"
                css={{ marginTop: 0, color: theme.brand.violet }}
              >
                <Data>{hero.heading}</Data>
              </HeroTitle>
            </RowColumn>
          </Container>
          <Container>
            <Row
              gutter={{ base: 'base', medium: 'xlarge' }}
              css={{
                'h2, h3, h4': [
                  {
                    color: theme.brand.violet,
                  },
                ],

                backgroundImage: `url(${BackgroundMobileSVG})`,
                backgroundPosition: '95% 65%',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100%',
                paddingBottom: '0px',

                [theme.mq.medium]: {
                  backgroundImage: `url(${BackgroundSVG})`,
                  backgroundPosition: '95% 90%',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '75%',
                  paddingBottom: '48px',
                },

                [theme.mq.large]: {
                  backgroundImage: `url(${BackgroundSVG})`,
                  backgroundPosition: '80% 80%',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '70%',
                  paddingBottom: '240px',
                },
              }}
            >
              <RowColumn
                size={{ medium: 6 }}
                css={{ order: 2, [theme.mq.medium]: { order: 1 } }}
              >
                <Image
                  src={bookImage}
                  css={{
                    display: 'block',

                    marginTop: theme.space.xlarge,
                    marginBottom: theme.space.xlarge,

                    [theme.mq.medium]: {
                      marginTop: theme.space['3xlarge'],
                      marginBottom: theme.space.xlarge,
                    },

                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                />
                <div
                  css={{
                    [theme.mq.small__max]: {
                      padding: theme.space.base,
                    },
                  }}
                >
                  <MDXProvider
                    components={{
                      ul: (props) => (
                        <List
                          space="base"
                          css={[listBullet(theme)]}
                          {...props}
                        />
                      ),
                    }}
                  >
                    <MDXRenderer>{leftColumnContent}</MDXRenderer>
                  </MDXProvider>
                </div>
              </RowColumn>
              <RowColumn
                size={{ medium: 6 }}
                css={{
                  order: 1,

                  [theme.mq.medium]: {
                    order: 2,
                    paddingTop: `0px !important`,
                  },
                }}
              >
                <div
                  css={{
                    strong: {
                      color: theme.brand.violet,
                    },

                    [theme.mq.small__max]: {
                      padding: theme.space.base,
                    },

                    paddingBottom: theme.space.large,
                  }}
                >
                  <MDXProvider
                    components={{
                      blockquote: (props) => (
                        <Quote contrastQuotes {...props} />
                      ),
                    }}
                  >
                    <MDXRenderer>{rightColumnContent}</MDXRenderer>
                  </MDXProvider>
                </div>
                <div
                  css={{
                    [theme.mq.medium]: {
                      paddingTop: theme.space['2xlarge'],
                    },
                  }}
                >
                  <HeroFormTitle
                    css={{
                      ...theme.headingPreset.h5,
                      textTransform: 'uppercase',
                      textAlign: 'left',
                      paddingTop: theme.space.large,
                      marginBottom: theme.space.base,

                      [theme.mq.small__max]: {
                        paddingLeft: theme.space.large,
                        paddingRight: theme.space.large,
                      },
                    }}
                  >
                    {hero.form.heading}
                  </HeroFormTitle>
                  <DownloadSVG
                    css={{
                      height: '20px',
                      position: 'relative',
                      left: '205px',
                      top: '-45px',

                      [theme.mq.small__max]: {
                        left: '190px',
                        top: '-57px',
                      },

                      [theme.mq.medium__max]: {
                        left: '210px',
                        top: '-43px',
                      },
                    }}
                  />
                </div>
                <HeroFormWrapper
                  css={{
                    backgroundColor: theme.brand.violet,
                    borderRadius: '0',
                    color: '#fff',
                  }}
                >
                  <MarketoForm
                    id={hero.form.embed}
                    fieldValues={{ programID: hero.form.programId }}
                    thankYouMessage={hero.form.thankYouMessage}
                    onSuccess={() => {
                      track('Content downloaded', {
                        label: `marketo-${hero.form.embed}`,
                      })
                    }}
                    css={{
                      '.Marketo__FieldWrapper': {
                        borderRadius: '0px',
                        boxShadow: 'none',
                        color: '#fff',
                        background: 'transparent',
                      },

                      'input.mktoField:not([type="checkbox"]):not([type="radio"]), input.mktoField:not([type="checkbox"]):not([type="radio"]):focus, .Marketo__Select select, .Marketo__Select select:focus': {
                        color: '#fff',
                      },

                      '.Marketo__Select select option': {
                        color: '#2F2256',
                      },

                      '.mktoRequiredField .Marketo__Select::after, .Marketo__Select::after': {
                        backgroundImage: `url(${CaretDoubleSVG})`,
                        filter: 'none',
                      },

                      '.Marketo__FieldWrapper.mktoRequiredField::after': {
                        backgroundImage: 'none',
                      },

                      '.Marketo__FieldWrapper.mktoRequiredField.Marketo__Error': {
                        borderColor: theme.color.danger.filter,
                        color: theme.color.danger.filter,
                      },

                      '.mktoButton, .mktoButton:hover, .mktoButton:focus': {
                        background: '#fff',
                        color: theme.brand.violet,
                        borderRadius: '0px',
                        width: '100%',
                      },
                    }}
                  />
                </HeroFormWrapper>
              </RowColumn>
            </Row>
          </Container>
        </HeroContent>
      </Hero>
      {!hideTeamIntro && (
        <TeamIntro
          cta={{
            label: hero.form.heading,
            url: '#form',
          }}
        />
      )}
      {showProductOverview && <ProductOverview />}
      {showFooterCTA && <FooterCTA />}
      <StaticFooter />
    </BaseLayout>
  )
}

BookLandingPageLayout.propTypes = {
  article: PropTypes.object.isRequired,
  nodeType: PropTypes.string.isRequired,
}

export default BookLandingPageLayout
